import { capitalize } from "lodash";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

export const EXPERIENCE_SORT_RANK: Record<string, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
  experienced: 3,
};

export const experienceSortFunction = ({
  data,
  order,
}: {
  data: Array<{ developerProfile?: { experience?: string } }>;
  order: number | null | undefined;
}) =>
  [...data].sort((a, b) => {
    const aRank = EXPERIENCE_SORT_RANK[a.developerProfile?.experience ?? ""] ?? -1;
    const bRank = EXPERIENCE_SORT_RANK[b.developerProfile?.experience ?? ""] ?? -1;
    return (order ?? 1) * (aRank - bRank);
  });

export const nullLastStringSort =
  (fieldPath: string) =>
  ({ data, order }: { data: unknown[]; order: number | null | undefined }) =>
    [...data].sort((a, b) => {
      const getValue = (obj: unknown): string =>
        (fieldPath
          .split(".")
          .reduce((o: unknown, k) => (o as Record<string, unknown>)?.[k], obj) as string) ?? "";
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return (order ?? 1) * aVal.localeCompare(bVal);
    });

export const toolingSortFunction = ({
  data,
  order,
}: {
  data: Array<{ developerProfile?: { topics?: string[] } }>;
  order: number | null | undefined;
}) =>
  [...data].sort((a, b) => {
    const aTopics = a.developerProfile?.topics;
    const bTopics = b.developerProfile?.topics;
    const isEmpty = (t?: string[]) => !t || t.length === 0;
    const isOtherOnly = (t?: string[]) => !!t?.length && t.every(v => v.toLowerCase() === "other");
    if (isEmpty(aTopics) && isEmpty(bTopics)) return 0;
    if (isEmpty(aTopics)) return 1;
    if (isEmpty(bTopics)) return -1;
    if (isOtherOnly(aTopics) && isOtherOnly(bTopics)) return 0;
    if (isOtherOnly(aTopics)) return 1;
    if (isOtherOnly(bTopics)) return -1;
    return (
      (order ?? 1) *
      (aTopics?.[0] ?? "").toLowerCase().localeCompare((bTopics?.[0] ?? "").toLowerCase())
    );
  });

// ---------------------------------------------------------------------------
// Column body render helpers (pure string functions — callers wrap with withSkeleton if needed)
// ---------------------------------------------------------------------------

type DeveloperProfile =
  | {
      jobFunction?: string;
      language?: string;
      experience?: string;
      expertise?: string;
      topics?: string[];
    }
  | boolean
  | null
  | undefined;

// The backend may return `false` for users who have no developer profile.
// Coerce boolean to undefined so callers can pass DirectoryUserShape.developerProfile directly.
const toProfile = (dp: DeveloperProfile) => (typeof dp === "object" ? dp : undefined);

export const devProfileJobFunction = (dp: DeveloperProfile): string => {
  const val = toProfile(dp)?.jobFunction;
  if (!val) return "";
  return capitalize(val.replace(/_/g, " "));
};

export const devProfileLanguage = (dp: DeveloperProfile): string => {
  const lang = toProfile(dp)?.language;
  if (!lang) return "";
  return (
    DeveloperProfileConstants.OPTIONS.programmingLanguages.find(o => o.value === lang.toLowerCase())
      ?.label ?? capitalize(lang)
  );
};

export const devProfileExperience = (dp: DeveloperProfile): string => {
  const exp = toProfile(dp)?.experience;
  if (!exp) return "";
  const opt = DeveloperProfileConstants.OPTIONS.experienceLevels.find(o => o.value === exp);
  return opt?.label?.split(" of")[0]?.trim() ?? exp;
};

export const devProfileExpertise = (dp: DeveloperProfile): string => {
  const val = toProfile(dp)?.expertise;
  if (!val) return "";
  return (
    DeveloperProfileConstants.OPTIONS.technicalExpertise.find(o => o.value === val)?.label ??
    capitalize(val.replace(/_/g, " "))
  );
};

export const devProfileTopics = (dp: DeveloperProfile): string => {
  const profile = toProfile(dp);
  if (!profile?.topics?.length) return "";
  return profile.topics
    .map(
      (t: string) =>
        DeveloperProfileConstants.OPTIONS.architectureTools.find(o => o.value === t)?.label ?? t
    )
    .join(", ");
};
